import { AxisCode } from '@atlas/types'
import { useState } from 'react'
import { optionLabel } from '@/components/classification/ClassifyPicker'
import type { Message, MessageListParams } from '@/domain/message.types'
import { MESSAGE_LIST_MAX_LIMIT } from '@/domain/message.types'
import type { Selection } from '@/domain/work-order.types'
import { type RailAxis, useAxesWithValues } from '@/hooks/useLibrary'
import { useMessages } from '@/hooks/useMessages'
import { sortedByLabel } from '@/lib/option-order'
import type { MatrixRow } from './matrix-model'
import { buildLocaleColumns, buildRow } from './matrix-model'
import type { MatrixAddRowAttachment } from './useMatrixAddRow'
import { useMatrixAddRow } from './useMatrixAddRow'
import { useMatrixBatchTranslate } from './useMatrixBatchTranslate'
import { useMatrixGroups } from './useMatrixGroups'
import { useMatrixRowEdit } from './useMatrixRowEdit'

/** Every locale-axis value as a picker option — the whole set a locale can be chosen from. */
function localeOptions(axes: RailAxis[]): { value: string; label: string }[] {
	const options = axes.find((a) => a.code === AxisCode.LOCALE)?.options ?? []
	return sortedByLabel(options.map((o) => ({ value: o.code, label: optionLabel(o.code, o.label) })))
}

/**
 * What a surface owning its own subset of messages hands the grid instead of the
 * global message list — a Campaign passes the copy associated with it, the
 * classification it already answers for, and the call that links a row added here
 * back to itself. Absent, the grid reads `GET /messages` under `filters` itself.
 */
export interface MatrixEmbedding {
	messages: readonly Message[]
	isLoading: boolean
	isError: boolean
	/** Copied onto a new row's draft, so the surface's own axes are not re-picked by hand. */
	seedClassification?: Selection
	attach?: MatrixAddRowAttachment
}

/**
 * Data + handlers behind `MessageResolutionMatrix`, composed the way
 * `useTranslationsPanel` sits behind `TranslationsSection`.
 *
 * Rows come from the message list narrowed to `isSource` rows: one source row IS
 * one piece of copy, and its id is the only one the translate endpoints accept as
 * a source. `GET /messages` has no `isSource` filter, so the list is read at the
 * endpoint's maximum page size and narrowed here — or, when `embedding` supplies
 * the rows, narrowed from those instead and the list read is not issued at all.
 *
 * That single page is a hard ceiling: the endpoint's maximum is the largest read
 * this surface makes, and the `isSource` narrowing then removes an unknown share
 * of it. A full page therefore means "there may be more", which `isTruncated`
 * carries to the grid rather than leaving the user to guess at a short list.
 */
export function useMessageResolutionMatrix(filters: MessageListParams, embedding?: MatrixEmbedding) {
	const list = useMessages({ ...filters, limit: MESSAGE_LIST_MAX_LIMIT }, { enabled: embedding === undefined })
	const { axes } = useAxesWithValues()
	const edit = useMatrixRowEdit()
	// Locales the user asked to see that no listed message covers yet: columns
	// only, no write, until a cell in one is translated or typed into.
	const [addedLocales, setAddedLocales] = useState<string[]>([])

	const sourceMessages = (embedding ? embedding.messages : (list.data ?? [])).filter((m) => m.isSource)
	const { groups, allVariants } = useMatrixGroups(sourceMessages)
	const localeColumns = buildLocaleColumns(allVariants, addedLocales)
	const rows: MatrixRow[] = sourceMessages.map((source) => {
		const group = groups.get(source.translationGroupId)
		return buildRow(source, group?.variants ?? [source], localeColumns, group?.isLoading ?? true)
	})
	const batch = useMatrixBatchTranslate(rows, edit.editingRowId)
	const locales = localeOptions(axes)
	const add = useMatrixAddRow({
		filters,
		axes,
		localeOptions: locales,
		defaultLocale: localeColumns[0] ?? '',
		seedClassification: embedding?.seedClassification,
		attach: embedding?.attach,
	})

	return {
		...batch,
		rows,
		localeColumns,
		isLoading: embedding ? embedding.isLoading : list.isLoading,
		isError: embedding ? embedding.isError : list.isError,
		// An embedding hands over its whole set, so only the list read can truncate.
		isTruncated: !embedding && (list.data?.length ?? 0) >= MESSAGE_LIST_MAX_LIMIT,
		availableLocaleOptions: locales.filter((o) => !localeColumns.includes(o.value)),
		addLocale: (locale: string) =>
			setAddedLocales((prev) => (localeColumns.includes(locale) ? prev : [...prev, locale])),
		edit,
		add,
	}
}
